import type { BoardMode } from '../core/types';
import type { ClientIdentity } from '../net/identity';
import {
  fetchMyScores, fetchSlayers, setMyName,
  type HistoryRow, type SlayerRow,
} from '../net/profile';
import { AI_ROSTER } from '../game/ai-roster';
import { fmtClock } from './board-view';

const MODE_LABEL: Record<BoardMode, string> = { daily: '日课', race: '日赛' };
const CAUSE_LABEL: Record<string, string> = {
  VICTORY: '通关', TIMEOUT: '时间耗尽', GREED: '贪刀',
  SURROUNDED: '被包围', CHARGED: '冲锋犬带走', BOMBED: '自爆菇带走',
  ELITE_COUNTER: '被精英克制', BOSS_SKILL: 'Boss 技能命中',
  ATTRITION: '消耗殆尽', SHIELD_CHIP: '护盾耗尽',
};

/** 菜单名号表单。 */
export function wireNameForm(
  input: HTMLInputElement, btn: HTMLElement, status: HTMLElement,
  idGetter: () => ClientIdentity | null,
  afterSet: (name: string) => void,
): void {
  const cur = idGetter()?.name ?? '';
  if (cur) { input.value = cur; status.textContent = `当前名号：${cur}`; }
  btn.addEventListener('click', async () => {
    const id = idGetter();
    if (!id) { status.textContent = '身份初始化中，请稍候再试'; return; }
    const name = input.value.trim();
    if (name.length < 1 || name.length > 12) {
      status.textContent = '名号需 1–12 个字符';
      return;
    }
    btn.setAttribute('disabled', '');
    status.textContent = '保存中…';
    try {
      await setMyName(id, name);
      afterSet(name);
      status.textContent = `名号已保存：${name}`;
    } catch (e) {
      status.textContent = `保存失败：${(e as Error).message}`;
    } finally {
      btn.removeAttribute('disabled');
    }
  });
}

function metaRow(
  rank: string, name: string, meta: string, cls = 'board-row',
): HTMLElement {
  const div = document.createElement('div');
  div.className = cls;
  const a = document.createElement('span');
  a.className = 'board-rank'; a.textContent = rank;
  const b = document.createElement('span');
  b.className = 'board-name'; b.textContent = name;
  const c = document.createElement('span');
  c.className = 'board-meta'; c.textContent = meta;
  div.append(a, b, c);
  return div;
}

/** 我的战绩屏。 */
export async function showHistory(
  list: HTMLElement, id: ClientIdentity,
): Promise<void> {
  list.textContent = '读取中…';
  let rows: HistoryRow[];
  try {
    rows = await fetchMyScores(id);
  } catch {
    list.textContent = '读取失败，请稍后再试。';
    return;
  }
  list.innerHTML = '';
  if (rows.length === 0) {
    list.textContent = '还没有正式成绩，去打一局留名吧。';
    return;
  }
  const best = rows.reduce((m, r) => Math.max(m, r.score), 0);
  const head = document.createElement('div');
  head.className = 'history-head';
  head.textContent = `历史最佳 ${best} · 共 ${rows.length} 局正式成绩`;
  list.append(head);
  for (const r of rows) {
    const cause = CAUSE_LABEL[r.cause] ?? r.cause;
    list.append(metaRow(
      r.date.slice(5),
      `${MODE_LABEL[r.mode]} · ${cause}`,
      `${r.score} · ${r.kills}杀 · ${fmtClock(r.time)}`,
    ));
  }
}

/** 屠榜殿堂屏。 */
export async function showSlayerHall(list: HTMLElement): Promise<void> {
  list.textContent = '读取中…';
  let rows: SlayerRow[];
  try {
    rows = await fetchSlayers();
  } catch {
    list.textContent = '读取失败，请稍后再试。';
    return;
  }
  list.innerHTML = '';
  if (rows.length === 0) {
    list.textContent = '还没有人击败过陪榜 AI，去创造历史。';
    return;
  }
  for (const r of rows) {
    const callsign = AI_ROSTER.find((x) => x.aiId === r.aiId)?.callsign
      ?? r.aiId;
    list.append(metaRow(
      r.date.slice(5),
      `${r.slayerName || '无名勇者'} 击败 [AI] ${callsign}`,
      `#${r.rank} 位 · ${r.version}`,
      'board-row board-row-me',
    ));
  }
}
